/**
 * Blocky Striker — pure scoring & collision predicates, shared by the game runtime and Vitest.
 *
 * The runtime is a self-contained HTML string (blockyStrikerGameHtml.ts) that Vitest cannot
 * import, so the §9 T-12 rule ("a goal only counts if the ball was kicked in") is enforced by
 * these pure functions: the runtime embeds their SOURCE via getBlockyStrikerScoringSource()
 * (Function.prototype.toString), and tests import them directly — one implementation, zero drift.
 *
 * Constraints for the embedding to stay valid: plain function declarations only, ES5-style
 * bodies (var, no arrow fns), no captured module-scope values except BLOCKY_STRIKER_SCORING
 * (which is injected into the runtime separately as JSON).
 *
 * All thresholds are the handoff's tuned values (specs/blocky-striker/handoff/README.md
 * §Collision / §Scoring) — do not retune here.
 */

/** Points / bonuses table (handoff Events table). Injected into the runtime as JSON. */
export const BLOCKY_STRIKER_SCORING = {
  GOAL_POINTS: 150,
  GOAL_TIME_BONUS_S: 8,
  SMASH_POINTS: 15,
  COIN_POINTS: 10,
  HEART_FULL_LIVES_POINTS: 25,
  CLOCK_TIME_BONUS_S: 5,
  DIST_STEP_M: 25,
  DIST_STEP_POINTS: 5,
  // v3
  NEAR_MISS_POINTS: 5,
  MISSION_BONUS_POINTS: 250,
} as const;

/**
 * Daily mission pool (v3 AC-32/33). Client-side and points-only by design — targets are the
 * per-UTC-day goals; `distance` is single-run, the rest accumulate across the day's runs.
 */
export const BLOCKY_STRIKER_MISSIONS = [
  { id: 'goals', target: 2, label: 'Score 2 kicked goals' },
  { id: 'smashes', target: 8, label: 'Smash 8 obstacles' },
  { id: 'coins', target: 25, label: 'Collect 25 coins' },
  { id: 'slides', target: 4, label: 'Slide under 4 obstacles' },
  { id: 'bounces', target: 3, label: 'Bounce 3 trampolines' },
  { id: 'distance', target: 700, label: 'Run 700 m in one run' },
] as const;

/**
 * Level campaign table (v4 AC-41..44). A level's identity is pure data — objective, speed
 * multiplier, and star thresholds; the procedural spawner is shared with Endless. Objective
 * types are the six run stats the runtime already counts. Extending the campaign = adding rows.
 */
export const BLOCKY_STRIKER_LEVELS = [
  // Pacing: early levels clear in ~20-40s (a quick win, but a RUN, not a blink), scaling toward
  // full-length challenges late. Stars are judged at the moment of the clear, so thresholds track
  // what a run of that length can actually earn.
  { n: 1, objective: { type: 'distance', target: 250 }, speedMult: 1.0, star2: 200, star3: 400 },
  { n: 2, objective: { type: 'coins', target: 10 }, speedMult: 1.04, star2: 300, star3: 550 },
  { n: 3, objective: { type: 'goals', target: 1 }, speedMult: 1.08, star2: 300, star3: 550 },
  { n: 4, objective: { type: 'smashes', target: 5 }, speedMult: 1.12, star2: 350, star3: 650 },
  { n: 5, objective: { type: 'distance', target: 450 }, speedMult: 1.16, star2: 450, star3: 800 },
  { n: 6, objective: { type: 'slides', target: 3 }, speedMult: 1.2, star2: 450, star3: 850 },
  { n: 7, objective: { type: 'goals', target: 2 }, speedMult: 1.24, star2: 600, star3: 1050 },
  { n: 8, objective: { type: 'coins', target: 18 }, speedMult: 1.28, star2: 650, star3: 1150 },
  { n: 9, objective: { type: 'bounces', target: 2 }, speedMult: 1.32, star2: 700, star3: 1250 },
  { n: 10, objective: { type: 'smashes', target: 8 }, speedMult: 1.36, star2: 750, star3: 1350 },
  { n: 11, objective: { type: 'distance', target: 700 }, speedMult: 1.4, star2: 850, star3: 1550 },
  { n: 12, objective: { type: 'goals', target: 3 }, speedMult: 1.45, star2: 950, star3: 1750 },
] as const;

/** Objective met when the matching run stat reaches the target (v4 AC-43). */
export function levelObjectiveMet(
  objective: { type: string; target: number },
  stats: Record<string, number>
): boolean {
  var v = stats[objective.type] || 0;
  return v >= objective.target;
}

/** Stars for a CLEARED level: 1 base, 2/3 at the level's points thresholds (v4 AC-43). */
export function levelStars(points: number, star2: number, star3: number): 1 | 2 | 3 {
  if (points >= star3) return 3;
  if (points >= star2) return 2;
  return 1;
}

/** Level 1 is free; every other level needs >= 1 star on the previous one (v4 AC-41). */
export function isLevelUnlocked(n: number, stars: Record<string, number>): boolean {
  if (n <= 1) return true;
  return (stars[n - 1] || 0) >= 1;
}

/**
 * Obstacle traits — the design rules per kind, shared by the spawner and the tests (§9 T-18).
 * Injected into the runtime as JSON (like the scoring table). `gapY > 0` marks an OVERHEAD
 * obstacle (clear space underneath — slide under it); `smashable` gates the kicked-ball smash;
 * `vz` is extra approach speed on top of the world scroll (moving threats).
 */
export const BLOCKY_STRIKER_OBSTACLES = {
  wall: { w: 1.9, h: 1.6, gapY: 0, smashable: true, vz: 0 },
  hedge: { w: 1.9, h: 1.1, gapY: 0, smashable: true, vz: 0 },
  crate: { w: 1.2, h: 1.2, gapY: 0, smashable: true, vz: 0 },
  mover: { w: 1.7, h: 1.3, gapY: 0, smashable: true, vz: 0 },
  fence: { w: 1.9, h: 0.9, gapY: 0, smashable: true, vz: 0 },
  spikes: { w: 1.9, h: 0.6, gapY: 0, smashable: false, vz: 0 },
  fire: { w: 1.9, h: 0.8, gapY: 0, smashable: false, vz: 0 },
  bar: { w: 1.9, h: 0.45, gapY: 1.15, smashable: false, vz: 0 },
  float: { w: 1.4, h: 0.9, gapY: 1.15, smashable: false, vz: 0 },
  runner: { w: 0.9, h: 1.9, gapY: 0, smashable: true, vz: 6 },
  roller: { w: 0.9, h: 0.8, gapY: 0, smashable: true, vz: 9 },
  // v3
  barrel: { w: 1.1, h: 1.2, gapY: 0, smashable: true, vz: 0 },
  cone: { w: 0.8, h: 0.7, gapY: 0, smashable: true, vz: 0 },
  bigwall: { w: 1.9, h: 2.6, gapY: 0, smashable: false, vz: 0 }, // trampoline-clearable only
  tramp: { w: 1.6, h: 0.3, gapY: 0, smashable: false, vz: 0, bounce: true }, // harmless pad
  flyer: { w: 0.8, h: 0.8, gapY: 1.0, smashable: true, vz: 10 }, // gapY/size vary per instance
} as const;

/**
 * Player vs obstacle. Returns 'hit' | 'cleared' | 'miss'.
 * Handoff: contact when |obs.z| < 0.8 and |obs.x - player.x| < w/2 + 0.35, only while
 * py < 1.0 and not invincible (invincibility is the caller's guard); a jump clears the
 * obstacle when py > 0.2 and py + 0.2 > obstacleHeight.
 *
 * v2: `obstacleGapY > 0` marks an overhead obstacle — passed ONLY by a grounded slide
 * (crouch height 0.7 fits under the gap); standing (≈1.9) or jumping contact is a hit, so the
 * overhead branch runs BEFORE the py >= 1.0 fly-over rule. Sliding never clears a ground
 * obstacle. Omitting the two new args reproduces v1 outcomes exactly.
 */
export function playerObstacleOutcome(
  obstacleX: number,
  obstacleZ: number,
  obstacleWidth: number,
  obstacleHeight: number,
  playerX: number,
  playerJumpHeight: number,
  sliding?: boolean,
  obstacleGapY?: number
): 'hit' | 'cleared' | 'miss' {
  if (Math.abs(obstacleZ) >= 0.8) return 'miss';
  if (Math.abs(obstacleX - playerX) >= obstacleWidth / 2 + 0.35) return 'miss';
  var gap = obstacleGapY || 0;
  if (gap > 0) {
    if (sliding && playerJumpHeight < 0.2 && gap > 0.7) return 'cleared';
    return 'hit';
  }
  // v3: the fly-over rule is height-aware — py >= 1.0 still clears any NORMAL-height obstacle
  // (h <= 1.6, byte-identical to v1/v2), but a big wall is only cleared above its own height,
  // which only a trampoline launch reaches (AC-25).
  if (playerJumpHeight >= 1.0 && (obstacleHeight <= 1.6 || playerJumpHeight + 0.2 > obstacleHeight)) return 'miss';
  if (playerJumpHeight > 0.2 && playerJumpHeight + 0.2 > obstacleHeight) return 'cleared';
  return 'hit';
}

/**
 * Trampoline bounce (v3 AC-24): automatic on grounded overlap with the pad — never a hit.
 * The launch velocity itself is a runtime tunable (vy 12, apex ≈ 3.3).
 */
export function trampolineBounces(
  padX: number,
  padZ: number,
  padWidth: number,
  playerX: number,
  playerJumpHeight: number
): boolean {
  return Math.abs(padZ) < 0.9 && Math.abs(padX - playerX) < padWidth / 2 + 0.4 && playerJumpHeight < 0.4;
}

/**
 * Near-miss (v3 AC-31): evaluated once, at the moment an obstacle passes the player unharmed —
 * a whisker outside the contact band (band = w/2 + 0.35), and not while flying over it.
 */
export function isNearMiss(
  obstacleX: number,
  obstacleWidth: number,
  playerX: number,
  playerJumpHeight: number
): boolean {
  var band = obstacleWidth / 2 + 0.35;
  var dx = Math.abs(obstacleX - playerX);
  return playerJumpHeight < 1.0 && dx >= band && dx < band + 0.75;
}

/**
 * Daily missions (v3 AC-32): pure and deterministic — the same UTC day key picks the same 3
 * distinct missions from the pool for every player, no server involved. The pool is passed in
 * (injected as JSON in the runtime) to keep this embeddable with zero module captures.
 */
export function pickDailyMissions<T>(dayKey: string, pool: readonly T[]): T[] {
  var h = 0;
  for (var i = 0; i < dayKey.length; i++) h = (h * 31 + dayKey.charCodeAt(i)) % 2147483647;
  var picked: T[] = [];
  var idx = h % pool.length;
  for (var k = 0; k < 3 && k < pool.length; k++) {
    while (picked.indexOf(pool[idx]) !== -1) idx = (idx + 1) % pool.length;
    picked.push(pool[idx]);
    idx = (idx + 1 + (h % 5)) % pool.length;
  }
  return picked;
}

/**
 * Support height under the player from a ramp unit (§9 T-16). A unit = an incline rising from
 * ground to `platformHeight` over `inclineLength`, then a flat platform of `platformLength`,
 * then a drop. `rampZ` is the world z of the incline's near (start) edge; the unit extends
 * toward -z and scrolls +z, so at rampZ = l the player (at z = 0) stands l units into the unit.
 */
export function rampSupportHeight(
  rampX: number,
  rampZ: number,
  rampWidth: number,
  inclineLength: number,
  platformLength: number,
  platformHeight: number,
  playerX: number
): number {
  if (Math.abs(rampX - playerX) >= rampWidth / 2 + 0.35) return 0;
  var l = rampZ;
  if (l <= 0) return 0;
  if (l < inclineLength) return (l / inclineLength) * platformHeight;
  if (l <= inclineLength + platformLength) return platformHeight;
  return 0;
}

/** Kicked ball vs obstacle (SMASH): |obs.z - ball.z| < 1.2 and |obs.x - ball.x| < w/2 + 0.5. */
export function ballSmashesObstacle(
  obstacleX: number,
  obstacleZ: number,
  obstacleWidth: number,
  ballX: number,
  ballZ: number
): boolean {
  return Math.abs(obstacleZ - ballZ) < 1.2 && Math.abs(obstacleX - ballX) < obstacleWidth / 2 + 0.5;
}

/**
 * THE kick-only goal rule (§9 T-12). A gate scores only while the ball is mid-kick:
 * kicked && |gate.z - ball.z| < 1.2 && |ball.x - gate.x| < gateHalfWidth.
 * A dribbled ball (kicked=false) NEVER scores, whatever the overlap.
 */
export function ballScoresGoal(
  gateX: number,
  gateZ: number,
  gateHalfWidth: number,
  ballX: number,
  ballZ: number,
  kicked: boolean
): boolean {
  if (!kicked) return false;
  return Math.abs(gateZ - ballZ) < 1.2 && Math.abs(ballX - gateX) < gateHalfWidth;
}

/**
 * Dribbling through a gate without kicking — scores 0 and earns the one-per-gate
 * "KICK it in to score!" nudge. The dribbled ball rests at z = -3.4, so the gate is
 * "at" the ball when |gate.z + 3.4| < 0.9.
 */
export function dribbleThroughGate(
  gateX: number,
  gateZ: number,
  gateHalfWidth: number,
  ballX: number,
  kicked: boolean
): boolean {
  if (kicked) return false;
  return Math.abs(gateZ + 3.4) < 0.9 && Math.abs(gateX - ballX) < gateHalfWidth;
}

/**
 * Pickup collection — by the player OR the ball, deliberately forgiving:
 * |z - ref.z| < 1.4 && |x - ref.x| < 1.2 (player ref z = 0).
 *
 * v2 (§9 T-17): pickups carry a rest height (`pickupY`, default 0.9 = ground hover). An
 * ELEVATED pickup (on a ramp platform, rest height > 1.5) collects only while the player is
 * at matching height, and never via the ball — the ball stays on the ground. 5-arg calls
 * behave exactly as v1.
 */
export function collectsPickup(
  pickupX: number,
  pickupZ: number,
  playerX: number,
  ballX: number,
  ballZ: number,
  pickupY?: number,
  playerY?: number
): boolean {
  var restY = typeof pickupY === 'number' ? pickupY : 0.9;
  var elevated = restY > 1.5;
  var refY = typeof playerY === 'number' ? playerY : 0;
  var heightOk = !elevated || Math.abs(restY - (refY + 0.9)) < 1.1;
  var nearPlayer = heightOk && Math.abs(pickupZ) < 1.4 && Math.abs(pickupX - playerX) < 1.2;
  var nearBall = !elevated && Math.abs(pickupZ - ballZ) < 1.4 && Math.abs(pickupX - ballX) < 1.2;
  return nearPlayer || nearBall;
}

/**
 * Canonical runtime identifier → predicate. The runtime's <script> calls these EXACT names, so each
 * must be bound to its canonical name in the embedded source. `Function.prototype.toString()` alone
 * is NOT enough: production minification (Metro/terser) mangles `Function.name`, so a bare
 * `fn.toString()` embeds `function o(...)` and the game loop then throws
 * `ReferenceError: playerObstacleOutcome is not defined` every frame — a run frozen at 0m. (Dev/
 * unminified builds keep the names, which is why this only bites in production.)
 */
const EMBEDDED_FNS = [
  ['playerObstacleOutcome', playerObstacleOutcome],
  ['ballSmashesObstacle', ballSmashesObstacle],
  ['ballScoresGoal', ballScoresGoal],
  ['dribbleThroughGate', dribbleThroughGate],
  ['collectsPickup', collectsPickup],
  ['rampSupportHeight', rampSupportHeight],
  ['trampolineBounces', trampolineBounces],
  ['isNearMiss', isNearMiss],
  ['pickDailyMissions', pickDailyMissions],
  ['levelObjectiveMet', levelObjectiveMet],
  ['levelStars', levelStars],
  ['isLevelUnlocked', isLevelUnlocked],
] as const;

/**
 * The predicates as embeddable JS source for the runtime's <script> block. Each is assigned to its
 * canonical name (`var NAME = <source>;`) so it resolves regardless of how minification renamed the
 * function itself — the assignment works whether toString() yields a named declaration, an anonymous
 * function, or an arrow. Bodies stay transform-proof per the header (ES5, no module captures).
 */
export function getBlockyStrikerScoringSource(): string {
  return EMBEDDED_FNS.map(([name, fn]) => `var ${name} = ${fn.toString()};`).join('\n');
}
